import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { getAuthHeaders } from '../utils/auth';
import {
  MarkdownEditor,
  MarkdownViewer,
  MultiEntityMentionsEditorRef,
} from './markdown';

import { API_URL } from '../constants/urls';

interface AccountPlan {
  id: string;
  organization_id: string;
  company_id: string;
  company_name?: string;
  account_narrative?: string;
  business_objectives?: string;
  key_challenges?: string;
  value_proposition_fit?: string;
  competitive_positioning?: string;
  hypotheses?: any[]; // JSONB field
  stakeholder_map?: any;
  expected_contract_value?: number;
  expected_close_date?: string;
  account_stage?: string;
  win_probability?: number;
  next_steps?: string;
  key_risks?: string;
  review_frequency?: 'weekly' | 'biweekly' | 'monthly' | 'quarterly';
  next_review_date?: string;
  created_at: string;
  updated_at: string;
  created_by_id?: string;
  updated_by_id?: string;
  created_by_name?: string;
  updated_by_name?: string;
}

interface Hypothesis {
  id: string;
  hypothesis: string;
  status: 'pending' | 'testing' | 'validated' | 'invalidated';
  validation_method?: string;
  success_criteria?: string;
  evidence?: string;
  target_completion_date?: string;
  confidence_level?: number;
  created_at: string;
  updated_at?: string;
  validated_at?: string;
  tasks?: string[];
}

interface StakeholderMapping {
  decision_makers?: Stakeholder[];
  influencers?: Stakeholder[];
  champions?: Stakeholder[];
  blockers?: Stakeholder[];
}

interface Stakeholder {
  name: string;
  role: string;
  influence_level: 'low' | 'medium' | 'high';
  engagement_status: 'not_engaged' | 'engaged' | 'champion' | 'detractor';
  notes?: string;
}

interface AccountPlanningDashboardProps {
  companyId: string;
  organizationId: string;
  canEdit: boolean;
  onAccountPlanUpdate?: (accountPlan: AccountPlan) => void;
}

export default function AccountPlanningDashboard({
  companyId,
  organizationId,
  canEdit,
  onAccountPlanUpdate,
}: AccountPlanningDashboardProps) {
  const [accountPlan, setAccountPlan] = useState<AccountPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editedPlan, setEditedPlan] = useState<Partial<AccountPlan>>({});

  // Editor refs for markdown fields
  const accountNarrativeRef = useRef<MultiEntityMentionsEditorRef>(null);
  const businessObjectivesRef = useRef<MultiEntityMentionsEditorRef>(null);
  const keyChallengesRef = useRef<MultiEntityMentionsEditorRef>(null);
  const valuePropositionRef = useRef<MultiEntityMentionsEditorRef>(null);
  const competitivePositioningRef = useRef<MultiEntityMentionsEditorRef>(null);
  const nextStepsRef = useRef<MultiEntityMentionsEditorRef>(null);
  const keyRisksRef = useRef<MultiEntityMentionsEditorRef>(null);

  // Hypotheses state
  const [hypotheses, setHypotheses] = useState<Hypothesis[]>([]);
  const [loadingHypotheses, setLoadingHypotheses] = useState(true);
  const [showAddHypothesisForm, setShowAddHypothesisForm] = useState(false);
  const [editingHypothesis, setEditingHypothesis] = useState<Hypothesis | null>(
    null,
  );
  const [hypothesisFormData, setHypothesisFormData] = useState<
    Partial<Hypothesis>
  >({
    hypothesis: '',
    status: 'pending',
    validation_method: '',
    success_criteria: '',
    evidence: '',
    target_completion_date: '',
    confidence_level: 50,
  });

  useEffect(() => {
    fetchAccountPlan();
    fetchHypotheses();
  }, [companyId, organizationId]);

  const fetchHypotheses = async () => {
    try {
      setLoadingHypotheses(true);
      console.log('Fetching hypotheses for company:', companyId);
      const response = await axios.get(
        `${API_URL}/companies/${companyId}/account-plan/hypotheses?organization_id=${organizationId}`,
        { headers: getAuthHeaders() },
      );
      console.log('Hypotheses received:', response.data);
      setHypotheses(response.data);
    } catch (error: any) {
      console.error('Error fetching hypotheses:', error);
      // If 404, assume no account plan exists yet - that's ok
      if (error.response?.status === 404) {
        console.log('No account plan exists yet, setting empty hypotheses');
        setHypotheses([]);
      }
    } finally {
      setLoadingHypotheses(false);
    }
  };

  const fetchAccountPlan = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/account-plans?organization_id=${organizationId}&company_id=${companyId}`,
        { headers: getAuthHeaders() },
      );
      console.log('Account plans found:', response.data);
      // The endpoint returns an array, get the first (and should be only) plan
      if (response.data && response.data.length > 0) {
        const plan = response.data[0];
        console.log('Account plan found:', plan);
        setAccountPlan(plan);
        setEditedPlan(plan);
      } else {
        // No account plan exists yet
        console.log('No account plan exists, setting up for creation');
        setAccountPlan(null);
        setEditedPlan({
          account_narrative: '',
          business_objectives: '',
          key_challenges: '',
          value_proposition_fit: '',
          competitive_positioning: '',
        });
      }
    } catch (error: any) {
      console.error('Error fetching account plans:', error);
      // On any error, set up for creation
      setAccountPlan(null);
      setEditedPlan({
        account_narrative: '',
        business_objectives: '',
        key_challenges: '',
        value_proposition_fit: '',
        competitive_positioning: '',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitHypothesis = async () => {
    try {
      // Clean up the data - convert empty strings to null for optional fields
      const dataToSubmit: any = {
        hypothesis: hypothesisFormData.hypothesis,
        status: hypothesisFormData.status || 'pending',
        validation_method: hypothesisFormData.validation_method || null,
        success_criteria: hypothesisFormData.success_criteria || null,
        evidence: hypothesisFormData.evidence || null,
        target_completion_date:
          hypothesisFormData.target_completion_date || null,
        confidence_level: hypothesisFormData.confidence_level ?? 50,
      };

      if (editingHypothesis) {
        // Update existing hypothesis
        console.log('Updating hypothesis:', editingHypothesis.id, dataToSubmit);
        const updateResponse = await axios.put(
          `${API_URL}/companies/${companyId}/account-plan/hypotheses/${editingHypothesis.id}?organization_id=${organizationId}`,
          dataToSubmit,
          { headers: getAuthHeaders() },
        );
        console.log('Update response:', updateResponse.data);
      } else {
        // Add new hypothesis
        console.log('Creating new hypothesis:', dataToSubmit);
        const createResponse = await axios.post(
          `${API_URL}/companies/${companyId}/account-plan/hypotheses?organization_id=${organizationId}`,
          dataToSubmit,
          { headers: getAuthHeaders() },
        );
        console.log('Create response:', createResponse.data);
      }

      // Refresh the list
      console.log('Refreshing hypotheses list...');
      await fetchHypotheses();

      // Reset form
      setShowAddHypothesisForm(false);
      setEditingHypothesis(null);
      setHypothesisFormData({
        hypothesis: '',
        status: 'pending',
        validation_method: '',
        success_criteria: '',
        evidence: '',
        target_completion_date: '',
        confidence_level: 50,
      });
    } catch (error: any) {
      console.error('Error saving hypothesis:', error);
      console.error('Error response:', error.response?.data);
      alert(
        `Error saving hypothesis: ${error.response?.data?.detail || error.message}`,
      );
    }
  };

  const handleDeleteHypothesis = async (hypothesisId: string) => {
    if (!window.confirm('Are you sure you want to delete this hypothesis?'))
      return;

    try {
      await axios.delete(
        `${API_URL}/companies/${companyId}/account-plan/hypotheses/${hypothesisId}?organization_id=${organizationId}`,
        { headers: getAuthHeaders() },
      );
      await fetchHypotheses();
    } catch (error) {
      console.error('Error deleting hypothesis:', error);
    }
  };

  const handleEditHypothesis = (hypothesis: Hypothesis) => {
    setEditingHypothesis(hypothesis);
    setHypothesisFormData(hypothesis);
    setShowAddHypothesisForm(true);
  };

  const getHypothesisStatusColor = (status: string) => {
    switch (status) {
      case 'validated':
        return 'bg-green-100 text-green-800';
      case 'invalidated':
        return 'bg-red-100 text-red-800';
      case 'testing':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSave = async () => {
    try {
      console.log(
        'Saving account plan. Current accountPlan:',
        accountPlan ? 'exists' : 'null',
      );
      console.log('Edited plan data:', editedPlan);

      // Get tokenized content from markdown editors
      const dataToSave = {
        ...editedPlan,
        account_narrative:
          accountNarrativeRef.current?.getTokenizedContent() ||
          editedPlan.account_narrative,
        business_objectives:
          businessObjectivesRef.current?.getTokenizedContent() ||
          editedPlan.business_objectives,
        key_challenges:
          keyChallengesRef.current?.getTokenizedContent() ||
          editedPlan.key_challenges,
        value_proposition_fit:
          valuePropositionRef.current?.getTokenizedContent() ||
          editedPlan.value_proposition_fit,
        competitive_positioning:
          competitivePositioningRef.current?.getTokenizedContent() ||
          editedPlan.competitive_positioning,
        next_steps:
          nextStepsRef.current?.getTokenizedContent() || editedPlan.next_steps,
        key_risks:
          keyRisksRef.current?.getTokenizedContent() || editedPlan.key_risks,
      };

      if (accountPlan) {
        // Update existing plan
        console.log('Updating existing account plan with ID:', accountPlan.id);
        const response = await axios.put(
          `${API_URL}/account-plans/${accountPlan.id}?organization_id=${organizationId}`,
          dataToSave,
          { headers: getAuthHeaders() },
        );
        setAccountPlan(response.data);
        if (onAccountPlanUpdate) {
          onAccountPlanUpdate(response.data);
        }
      } else {
        // Create new plan
        console.log('Creating new account plan for company:', companyId);
        const response = await axios.post(
          `${API_URL}/account-plans?organization_id=${organizationId}`,
          {
            ...dataToSave,
            company_id: companyId,
          },
          { headers: getAuthHeaders() },
        );
        setAccountPlan(response.data);
        if (onAccountPlanUpdate) {
          onAccountPlanUpdate(response.data);
        }
      }
      setEditMode(false);
    } catch (error: any) {
      console.error('Error saving account plan:', error);
      console.error('Error response:', error.response?.data);
      alert(
        `Error saving account plan: ${error.response?.data?.detail || error.message}`,
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading account plan...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Account Narrative Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Account Narrative</h3>
          {canEdit && !editMode && (
            <button
              onClick={() => {
                setEditMode(true);
                setEditedPlan(accountPlan || {});
              }}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Edit Plan
            </button>
          )}
          {canEdit && editMode && (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setEditMode(false);
                  setEditedPlan(accountPlan || {});
                }}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          )}
        </div>

        {editMode ? (
          <MarkdownEditor
            ref={accountNarrativeRef}
            value={editedPlan.account_narrative || ''}
            onChange={(value) =>
              setEditedPlan({
                ...editedPlan,
                account_narrative: value,
              })
            }
            organizationId={organizationId}
            placeholder="Describe the overall strategic narrative for this account... (@ to link)"
          />
        ) : accountPlan?.account_narrative ? (
          <MarkdownViewer content={accountPlan.account_narrative} />
        ) : (
          <p className="text-gray-400 italic">
            No account narrative defined yet
          </p>
        )}
      </div>

      {/* Business Context */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Business Objectives</h3>
          {editMode ? (
            <MarkdownEditor
              ref={businessObjectivesRef}
              value={editedPlan.business_objectives || ''}
              onChange={(value) =>
                setEditedPlan({
                  ...editedPlan,
                  business_objectives: value,
                })
              }
              organizationId={organizationId}
              placeholder="What are their key business objectives? (@ to link)"
            />
          ) : accountPlan?.business_objectives ? (
            <MarkdownViewer content={accountPlan.business_objectives} />
          ) : (
            <p className="text-gray-400 italic">Not defined</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Key Challenges</h3>
          {editMode ? (
            <MarkdownEditor
              ref={keyChallengesRef}
              value={editedPlan.key_challenges || ''}
              onChange={(value) =>
                setEditedPlan({ ...editedPlan, key_challenges: value })
              }
              organizationId={organizationId}
              placeholder="What are their main challenges?"
            />
          ) : accountPlan?.key_challenges ? (
            <MarkdownViewer content={accountPlan.key_challenges} />
          ) : (
            <p className="text-gray-400 italic">Not defined</p>
          )}
        </div>
      </div>

      {/* Strategic Positioning */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Value Proposition Fit</h3>
          {editMode ? (
            <MarkdownEditor
              ref={valuePropositionRef}
              value={editedPlan.value_proposition_fit || ''}
              onChange={(value) =>
                setEditedPlan({
                  ...editedPlan,
                  value_proposition_fit: value,
                })
              }
              organizationId={organizationId}
              placeholder="How do our propositions fit their needs?"
            />
          ) : accountPlan?.value_proposition_fit ? (
            <MarkdownViewer content={accountPlan.value_proposition_fit} />
          ) : (
            <p className="text-gray-400 italic">Not defined</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">
            Competitive Positioning
          </h3>
          {editMode ? (
            <MarkdownEditor
              ref={competitivePositioningRef}
              value={editedPlan.competitive_positioning || ''}
              onChange={(value) =>
                setEditedPlan({
                  ...editedPlan,
                  competitive_positioning: value,
                })
              }
              organizationId={organizationId}
              placeholder="How are we positioned vs competitors?"
            />
          ) : accountPlan?.competitive_positioning ? (
            <MarkdownViewer content={accountPlan.competitive_positioning} />
          ) : (
            <p className="text-gray-400 italic">Not defined</p>
          )}
        </div>
      </div>

      {/* Action Planning */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Next Steps</h3>
          {editMode ? (
            <MarkdownEditor
              ref={nextStepsRef}
              value={editedPlan.next_steps || ''}
              onChange={(value) =>
                setEditedPlan({ ...editedPlan, next_steps: value })
              }
              organizationId={organizationId}
              placeholder="What are the immediate next steps for this account? (@ to link)"
            />
          ) : accountPlan?.next_steps ? (
            <MarkdownViewer content={accountPlan.next_steps} />
          ) : (
            <p className="text-gray-400 italic">No next steps defined</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Key Risks</h3>
          {editMode ? (
            <MarkdownEditor
              ref={keyRisksRef}
              value={editedPlan.key_risks || ''}
              onChange={(value) =>
                setEditedPlan({ ...editedPlan, key_risks: value })
              }
              organizationId={organizationId}
              placeholder="What are the key risks or concerns for this account?"
            />
          ) : accountPlan?.key_risks ? (
            <MarkdownViewer content={accountPlan.key_risks} />
          ) : (
            <p className="text-gray-400 italic">No risks identified</p>
          )}
        </div>
      </div>

      {/* Review Planning */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Review Frequency</h3>
          {editMode ? (
            <select
              value={editedPlan.review_frequency || ''}
              onChange={(e) =>
                setEditedPlan({
                  ...editedPlan,
                  review_frequency: e.target.value as any,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">Not set</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          ) : (
            <p className="text-gray-700">
              {accountPlan?.review_frequency ? (
                <span className="capitalize">
                  {accountPlan.review_frequency.replace(
                    'biweekly',
                    'bi-weekly',
                  )}
                </span>
              ) : (
                <span className="text-gray-400 italic">Not set</span>
              )}
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Next Review Date</h3>
          {editMode ? (
            <input
              type="date"
              value={editedPlan.next_review_date || ''}
              onChange={(e) =>
                setEditedPlan({
                  ...editedPlan,
                  next_review_date: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          ) : (
            <p className="text-gray-700">
              {accountPlan?.next_review_date ? (
                new Date(accountPlan.next_review_date).toLocaleDateString()
              ) : (
                <span className="text-gray-400 italic">Not scheduled</span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Last Updated */}
      {accountPlan && (
        <div className="text-sm text-gray-500 text-right">
          Last updated {formatDistanceToNow(new Date(accountPlan.updated_at))}{' '}
          ago
        </div>
      )}

      {/* Hypotheses Section */}
      <div className="mt-8 space-y-6">
        {/* Hypotheses Header */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Hypotheses ({hypotheses.length})
          </h3>
          {canEdit && (
            <button
              onClick={() => setShowAddHypothesisForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Add Hypothesis
            </button>
          )}
        </div>

        {/* Add/Edit Hypothesis Form */}
        {showAddHypothesisForm && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
            <h4 className="font-semibold">
              {editingHypothesis ? 'Edit Hypothesis' : 'New Hypothesis'}
            </h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hypothesis Statement *
              </label>
              <textarea
                value={hypothesisFormData.hypothesis || ''}
                onChange={(e) =>
                  setHypothesisFormData({
                    ...hypothesisFormData,
                    hypothesis: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows={3}
                placeholder="We believe that..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={hypothesisFormData.status || 'pending'}
                  onChange={(e) =>
                    setHypothesisFormData({
                      ...hypothesisFormData,
                      status: e.target.value as any,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="pending">Pending</option>
                  <option value="testing">Testing</option>
                  <option value="validated">Validated</option>
                  <option value="invalidated">Invalidated</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Completion Date
                </label>
                <input
                  type="date"
                  value={hypothesisFormData.target_completion_date || ''}
                  onChange={(e) =>
                    setHypothesisFormData({
                      ...hypothesisFormData,
                      target_completion_date: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Validation Method
              </label>
              <textarea
                value={hypothesisFormData.validation_method || ''}
                onChange={(e) =>
                  setHypothesisFormData({
                    ...hypothesisFormData,
                    validation_method: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows={2}
                placeholder="How will we validate this hypothesis?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Success Criteria
              </label>
              <textarea
                value={hypothesisFormData.success_criteria || ''}
                onChange={(e) =>
                  setHypothesisFormData({
                    ...hypothesisFormData,
                    success_criteria: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows={2}
                placeholder="What defines success?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Evidence
              </label>
              <textarea
                value={hypothesisFormData.evidence || ''}
                onChange={(e) =>
                  setHypothesisFormData({
                    ...hypothesisFormData,
                    evidence: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows={3}
                placeholder="Supporting evidence or findings..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confidence Level ({hypothesisFormData.confidence_level || 50}%)
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={hypothesisFormData.confidence_level || 50}
                onChange={(e) =>
                  setHypothesisFormData({
                    ...hypothesisFormData,
                    confidence_level: parseInt(e.target.value),
                  })
                }
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0% - Low</span>
                <span>50% - Medium</span>
                <span>100% - High</span>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowAddHypothesisForm(false);
                  setEditingHypothesis(null);
                  setHypothesisFormData({
                    hypothesis: '',
                    status: 'pending',
                    validation_method: '',
                    success_criteria: '',
                    evidence: '',
                    target_completion_date: '',
                    confidence_level: 50,
                  });
                }}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitHypothesis}
                disabled={!hypothesisFormData.hypothesis}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {editingHypothesis ? 'Update' : 'Add'} Hypothesis
              </button>
            </div>
          </div>
        )}

        {/* Hypotheses List */}
        <div className="space-y-4">
          {loadingHypotheses ? (
            <div className="flex justify-center items-center h-32">
              <div className="text-gray-500">Loading hypotheses...</div>
            </div>
          ) : hypotheses.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-500">
                No hypotheses yet. Add your first hypothesis to get started.
              </p>
            </div>
          ) : (
            hypotheses.map((hypothesis) => (
              <div
                key={hypothesis.id}
                className="bg-white border border-gray-200 rounded-lg p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h4 className="font-semibold text-lg mb-2">
                      {hypothesis.hypothesis}
                    </h4>
                    <div className="flex items-center gap-4 text-sm">
                      <span
                        className={`px-2 py-1 rounded ${getHypothesisStatusColor(hypothesis.status)}`}
                      >
                        {hypothesis.status}
                      </span>
                      {hypothesis.confidence_level !== undefined &&
                        hypothesis.confidence_level !== null && (
                          <span className="text-gray-600">
                            Confidence: {hypothesis.confidence_level}%
                          </span>
                        )}
                      {hypothesis.target_completion_date && (
                        <span className="text-gray-600">
                          Target:{' '}
                          {new Date(
                            hypothesis.target_completion_date,
                          ).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditHypothesis(hypothesis)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteHypothesis(hypothesis.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>

                {hypothesis.validation_method && (
                  <div className="mb-3">
                    <h5 className="font-medium text-sm text-gray-700 mb-1">
                      Validation Method
                    </h5>
                    <p className="text-gray-600 text-sm">
                      {hypothesis.validation_method}
                    </p>
                  </div>
                )}

                {hypothesis.success_criteria && (
                  <div className="mb-3">
                    <h5 className="font-medium text-sm text-gray-700 mb-1">
                      Success Criteria
                    </h5>
                    <p className="text-gray-600 text-sm">
                      {hypothesis.success_criteria}
                    </p>
                  </div>
                )}

                {hypothesis.evidence && (
                  <div className="mb-3">
                    <h5 className="font-medium text-sm text-gray-700 mb-1">
                      Evidence
                    </h5>
                    <p className="text-gray-600 text-sm">
                      {hypothesis.evidence}
                    </p>
                  </div>
                )}

                <div className="text-xs text-gray-500 mt-4">
                  Created {formatDistanceToNow(new Date(hypothesis.created_at))}{' '}
                  ago
                  {hypothesis.validated_at && (
                    <span className="ml-2">
                      • Validated{' '}
                      {formatDistanceToNow(new Date(hypothesis.validated_at))}{' '}
                      ago
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
